import type { QuoteRequest, QuoteResults } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Download, ExternalLink } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { callAction } from "../api";
import { JobCard } from "../components/JobCard";
import { ModeBadge } from "../components/ModeBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";

export function QuoteScreen() {
  const { quoteRequestId = "" } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["quote", quoteRequestId];
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await callAction<"agency.quote.results", QuoteResults>("agency.quote.results", { quoteRequestId })).result,
    refetchInterval: (query) => {
      const s = query.state.data?.quoteRequest.status;
      const j = query.state.data?.job?.status;
      return s === "pending" || s === "partial" || j === "needs_human" ? 2000 : false;
    },
  });
  const select = useMutation({
    mutationFn: (offerId: string) => callAction<"agency.quote.select", QuoteRequest>("agency.quote.select", { quoteRequestId, offerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      toast({ title: t("quote.selected"), body: t("quote.selectNote") });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const qr = d.quoteRequest;
  const app = d.application;
  const isFixture = qr.executionMode === "fixture";
  const returned = qr.offers.filter((o) => o.premiumCents != null).sort((a, b) => a.premiumCents! - b.premiumCents!);
  const cheapest = returned[0]?.id;
  const inProgress = qr.status === "pending" || qr.status === "partial";

  const downloadXml = () => {
    if (!d.acordXml) return;
    const blob = new Blob([d.acordXml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${d.customerName.replace(/\s+/g, "_")}_app_v${app.version}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <p className="text-sm text-ink/60"><Link to={`/customers/${qr.customerId}`} className="hover:underline">{d.customerName}</Link> · {t("quote.version")}{qr.applicationVersion} · {shortDate(qr.requestedAt, locale)}</p>
          <h1 className="text-2xl font-bold">{t("quote.title")} · {app.line.toUpperCase()} {app.state}</h1>
        </div>
        <ModeBadge mode={qr.executionMode} long />
        <Badge tone={inProgress ? "amber" : qr.status === "failed" ? "red" : "green"}>{inProgress ? t("quote.waiting") : qr.status === "failed" ? t("quote.error") : t("quote.allBack")}</Badge>
      </div>

      {d.job ? <JobCard job={d.job} invalidate={key} /> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader title={t("quote.completeness")} />
          <div className="px-5 pb-5 text-sm">
            {app.missingFields.length === 0 ? (
              <p className="inline-flex items-center gap-2 text-primary-dark font-semibold"><CheckCircle2 className="h-4 w-4" />{t("quote.complete")}</p>
            ) : (
              <>
                <p className="font-semibold text-amber-700 inline-flex items-center gap-2"><AlertCircle className="h-4 w-4" />{t("quote.missing")}</p>
                <ul className="mt-1 list-disc pl-5 text-ink/70">{app.missingFields.map((m) => <li key={m}>{m}</li>)}</ul>
              </>
            )}
          </div>
        </Card>
        <Card className="md:col-span-2">
          <CardHeader title={t("quote.baseline")} />
          <dl className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            {Object.entries(d.baseline).map(([k, v]) => (
              <div key={k}>
                <dt className="text-ink/50 text-xs">{k}</dt>
                <dd className="font-semibold">{fmtVal(v)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {d.deepLink ? (
          <Button variant="outline" onClick={() => window.open(d.deepLink!, "_blank", "noopener")} title={isFixture ? t("quote.fixtureLink") : undefined}>
            <ExternalLink className="h-4 w-4" /> {t("quote.openTurboRater")} {isFixture ? <Badge>{t("mode.fixture")}</Badge> : null}
          </Button>
        ) : null}
        {d.acordXml ? (
          <Button variant="outline" onClick={downloadXml} title={isFixture ? t("quote.fixtureLink") : undefined}>
            <Download className="h-4 w-4" /> {t("quote.downloadXml")} {isFixture ? <Badge>{t("mode.fixture")}</Badge> : null}
          </Button>
        ) : null}
        {isFixture && (d.deepLink || d.acordXml) ? <p className="w-full text-xs text-ink/55">{t("quote.fixtureLink")}</p> : null}
      </div>

      <section>
        <h2 className="text-lg font-bold mb-2">{t("quote.offers")}</h2>
        <p className="text-xs text-ink/55 mb-3">{t("quote.selectNote")}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {qr.offers.map((o) => {
            const selected = qr.selectedOfferId === o.id;
            const differs = o.differences.length > 0;
            return (
              <Card key={o.id} className={cn("p-4 flex flex-col gap-2", selected && "ring-2 ring-primary", differs && "border-accent/60")}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-bold">{o.carrier}</p>
                    <p className="text-xs text-ink/55">{o.status === "pending" ? t("quote.pending") : o.status === "returned" ? t("quote.returned") : o.status === "declined" ? t("quote.declined") : t("quote.error")}{o.returnedAt ? ` · ${new Date(o.returnedAt).toLocaleTimeString(locale === "es" ? "es-MX" : "en-US", { hour: "numeric", minute: "2-digit" })}` : ""}</p>
                  </div>
                  <ModeBadge mode={qr.executionMode} />
                </div>
                {o.status === "pending" ? <div className="h-10 flex items-center text-ink/40"><span className="h-4 w-4 animate-spin rounded-full border-2 border-ink/20 border-t-primary mr-2" />{t("quote.pending")}</div> : null}
                {o.status === "returned" ? (
                  <p className="text-3xl font-heading font-bold">
                    {money(o.premiumCents)} <span className="text-sm text-ink/55 font-body font-normal">/ {o.termMonths} {t("quote.months")}</span>
                    {o.id === cheapest && !differs ? <Badge tone="green" className="ml-2 align-middle">{t("today.best")}</Badge> : null}
                  </p>
                ) : null}
                {o.status === "declined" || o.status === "error" ? <p className="text-ink/60 h-10 flex items-center">—</p> : null}
                {differs ? (
                  <div className="rounded-xl bg-accent/15 p-2 text-xs text-amber-900">
                    <p className="font-bold inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{t("quote.differences")}</p>
                    <ul className="list-disc pl-4 mt-1">{o.differences.map((x) => <li key={x}>{x}</li>)}</ul>
                  </div>
                ) : null}
                <div className="mt-auto pt-1">
                  <Button variant={selected ? "secondary" : "outline"} size="sm" className="w-full" disabled={o.status !== "returned"} loading={select.isPending} onClick={() => select.mutate(o.id)}>
                    {selected ? <><CheckCircle2 className="h-4 w-4" />{t("quote.selected")}</> : t("quote.select")}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function fmtVal(v: unknown): string {
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (typeof v === "number") return v >= 1000 ? `$${v.toLocaleString("en-US")}` : String(v);
  if (v == null) return "—";
  return String(v);
}
